import { Box, Button, Card, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TierManagementPlanResponse } from '../../services/subscriptionPaymentService';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { enqueueSnackbar } from '../../store/app-slice/app';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatSubscriptionPrice } from '../../utils/subscriptionFormatting';
import { Header } from '../common/Header';
import { TierPlanEditorDialog } from './TierPlanEditorDialog';
import {
    closeTierPlanEditor,
    fetchTierPlans,
    openTierPlanEditor,
    saveTierPlanChanges,
    setTierDescription,
    setTierModelsAllowed,
    setTierModelsAllowedEnabled,
    setTierName,
    setTierTrialDuration,
    setTierTrialSupported,
    setTierUnitAmount,
    tierManagementSelector,
} from '../../store/tiermanagement-slice/tiermanagement-slice';

type TierPlan = TierManagementPlanResponse;
const TRIAL_DURATION_ONE_WEEK = '1_week';
const TRIAL_DURATION_TWO_WEEKS = '2_weeks';
const TRIAL_DURATION_ONE_MONTH = '1_month';

const normalizeTrialDuration = (value?: string | null): string => {
    if (value === TRIAL_DURATION_ONE_WEEK || value === TRIAL_DURATION_TWO_WEEKS || value === TRIAL_DURATION_ONE_MONTH) {
        return value;
    }

    return TRIAL_DURATION_ONE_MONTH;
};

const createColumns = (
    t: (value: string) => string,
    onEdit: (plan: TierPlan) => void,
    formatPlanPrice: (plan: TierPlan) => string,
    formatTrialDuration: (duration?: string | null) => string
): GridColDef<TierPlan>[] => [
    {
        field: 'name',
        headerName: t('TIER_MANAGEMENT_NAME'),
        minWidth: 210,
        flex: 1,
        renderCell: (params) => (
            <Stack spacing={0.1} sx={{ py: 0.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {params.row.name || '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--link-text-color)' }}>
                    {params.row.stripePriceId}
                </Typography>
            </Stack>
        ),
    },
    {
        field: 'description',
        headerName: t('TIER_MANAGEMENT_DESCRIPTION'),
        minWidth: 260,
        flex: 1.2,
        renderCell: (params) => (
            <Typography
                variant="body2"
                sx={{
                color: 'var(--link-text-color)',
                py: 0.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                }}
            >
                {params.row.description || t('NO_DATA_LABEL')}
            </Typography>
        ),
    },
    {
        field: 'billing',
        headerName: t('PROFILE_BILLING_TITLE'),
        minWidth: 200,
        flex: 0.9,
        renderCell: (params) => (
            <Stack spacing={0.1} sx={{ py: 0.2 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatPlanPrice(params.row as TierPlan)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--link-text-color)' }}>
                    {params.row.billingInterval ? `${t('PROFILE_PLANS_PER_LABEL')} ${t(params.row.billingInterval)}` : '-'}
                </Typography>
            </Stack>
        ),
    },
    {
        field: 'modelsAllowed',
        headerName: t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL'),
        minWidth: 170,
        renderCell: (params) => (
            <Typography variant="body2">
                {params.row.modelsAllowed == null
                ? t('SUBSCRIPTION_MODELS_ALLOWED_UNLIMITED')
                : params.row.modelsAllowed}
            </Typography>
        ),
    },
    {
        field: 'trial',
        headerName: t('TIER_MANAGEMENT_TRIAL_SUPPORTED'),
        minWidth: 190,
        flex: 0.9,
        sortable: false,
        renderCell: (params) => (
            <Stack spacing={0.3} sx={{ py: 0.15 }}>
                <Chip
                    size="small"
                    label={params.row.trialSupported ? t('YES_LABEL') : t('NO_LABEL')}
                    variant={params.row.trialSupported ? 'filled' : 'outlined'}
                    sx={
                        params.row.trialSupported
                            ? {
                                backgroundColor: 'var(--button-background-color)',
                                color: 'var(--button-text-color)',
                                fontWeight: 600,
                            }
                            : {
                                borderColor: 'var(--selected-table-row-color)',
                                color: 'var(--link-text-color)',
                            }
                    }
                />
                <Typography variant="caption" sx={{ color: 'var(--link-text-color)' }}>
                    {params.row.trialSupported
                        ? formatTrialDuration(params.row.trialDuration)
                        : '-'}
                </Typography>
            </Stack>
        ),
    },
    {
        field: 'active',
        headerName: t('USERMANAGEMENT_ENABLED_COLUMN'),
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (
            <Chip
                size="small"
                label={params.row.active ? t('YES_LABEL') : t('NO_LABEL')}
                variant={params.row.active ? 'filled' : 'outlined'}
                sx={
                params.row.active
                    ? {
                        backgroundColor: 'var(--button-background-color)',
                        color: 'var(--button-text-color)',
                        fontWeight: 600,
                    }
                    : {
                        borderColor: 'var(--selected-table-row-color)',
                        color: 'var(--link-text-color)',
                    }
                }
            />
        ),
    },
    {
        field: 'actions',
        headerName: t('USERMANAGEMENT_ACTION_COLUMN'),
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Tooltip title={t('TIER_MANAGEMENT_EDIT')}>
                <IconButton aria-label="edit-tier" onClick={() => onEdit(params.row as TierPlan)}>
                    <EditIcon className="button-text-color" />
                </IconButton>
            </Tooltip>
        ),
    },
];

export default function TierManagement() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        plans,
        loading,
        saving,
        editPlan,
        name,
        description,
        unitAmount,
        modelsAllowedEnabled,
        modelsAllowed,
        trialSupported,
        trialDuration,
    } = useAppSelector(tierManagementSelector);

    const sendFeedback = useCallback((message: string, isError = false) => {
        dispatch(enqueueSnackbar({
            message: t(message),
            options: { variant: isError ? 'error' : 'success' },
        }));
    }, [dispatch, t]);

    const loadPlans = useCallback(async () => {
        const result = await dispatch(fetchTierPlans());
        if (fetchTierPlans.rejected.match(result)) {
            sendFeedback(result.payload || 'BE_COMMON_UNHANDLED_ERROR', true);
        }
    }, [dispatch, sendFeedback]);

    useEffect(() => {
        void loadPlans();
    }, [loadPlans]);

    const handleEdit = useCallback((plan: TierPlan) => {
        dispatch(openTierPlanEditor(plan));
    }, [dispatch]);

    const formatPlanPrice = useCallback((plan: TierPlan) => {
        return formatSubscriptionPrice(plan.unitAmount, plan.currency) ?? t('PROFILE_PLAN_PRICE_CONTACT');
    }, [t]);

    const formatTrialDuration = useCallback((duration?: string | null) => {
        const normalized = normalizeTrialDuration(duration);
        if (normalized === TRIAL_DURATION_ONE_WEEK) return t('TIER_MANAGEMENT_TRIAL_DURATION_1_WEEK');
        if (normalized === TRIAL_DURATION_TWO_WEEKS) return t('TIER_MANAGEMENT_TRIAL_DURATION_2_WEEKS');
        return t('TIER_MANAGEMENT_TRIAL_DURATION_1_MONTH');
    }, [t]);

    const columns = useMemo(
        () => createColumns(t, handleEdit, formatPlanPrice, formatTrialDuration),
        [t, handleEdit, formatPlanPrice, formatTrialDuration]
    );

    const activePlansCount = useMemo(() => plans.filter((plan: TierPlan) => plan.active).length, [plans]);
    const currencyLabel = useMemo(() => {
        const currencies = Array.from(new Set(plans.map((plan: TierPlan) => plan.currency).filter(Boolean)));
        return currencies.length > 0 ? currencies.join(', ') : '-';
    }, [plans]);

    const editCurrency = (editPlan?.currency || '').toUpperCase();
    const currencySymbol = useMemo(() => {
        if (!editCurrency) return '';

        try {
            const parts = new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: editCurrency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).formatToParts(0);
            return parts.find((part) => part.type === 'currency')?.value ?? editCurrency;
        } catch {
            return editCurrency;
        }
    }, [editCurrency]);

    const closeDialog = () => {
        dispatch(closeTierPlanEditor());
    };

    const handleSave = async () => {
        const result = await dispatch(saveTierPlanChanges());

        if (saveTierPlanChanges.rejected.match(result)) {
            sendFeedback(result.payload || 'BE_COMMON_UNHANDLED_ERROR', true);
            return;
        }

        if (result.payload.updated) {
            dispatch(enqueueSnackbar({
                message: t('TIER_MANAGEMENT_UPDATE_SUCCESS', { count: result.payload.updatedSubscriptionsCount }),
                options: { variant: 'success' },
            }));
        }
    };

    return (
        <Box sx={{ maxWidth: 1480, margin: '0 auto', display: 'grid', gap: 2.5 }}>
        <Card elevation={3} sx={{ borderRadius: 2.5, overflow: 'hidden', border: '1px solid var(--selected-table-row-color)' }}>
            <Header title={t('TIER_MANAGEMENT_TITLE')} />
            <Box sx={{ px: { xs: 1.5, md: 2.25 }, py: { xs: 1.25, md: 1.5 }, borderBottom: '1px solid var(--selected-table-row-color)', backgroundColor: 'action.hover' }}>
            <Stack spacing={1.2}>
                <Typography variant="body2" sx={{ maxWidth: 860, color: 'var(--link-text-color)' }}>
                    {t('TIER_MANAGEMENT_DESCRIPTION_TEXT')}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                        icon={<AutoAwesomeIcon />}
                        label={t('TIER_MANAGEMENT_TOTAL_PLANS', { count: plans.length })}
                        variant="outlined"
                        size="small"
                        sx={{
                        borderColor: 'var(--selected-table-row-color)',
                        color: 'var(--link-text-color)',
                        '& .MuiChip-icon': { color: 'var(--link-text-color)' },
                        }}
                    />
                    <Chip
                        label={t('TIER_MANAGEMENT_ACTIVE_PLANS', { count: activePlansCount })}
                        size="small"
                        sx={{
                        backgroundColor: 'var(--button-text-color)',
                        color: 'var(--button-background-color)',
                        fontWeight: 700,
                        }}
                    />
                    <Chip
                        label={t('TIER_MANAGEMENT_CURRENCY', { currency: currencyLabel })}
                        size="small"
                        variant="outlined"
                        sx={{
                        borderColor: 'var(--selected-table-row-color)',
                        color: 'var(--link-text-color)',
                        }}
                    />
                </Stack>
            </Stack>
            </Box>

            <Divider sx={{ borderColor: 'var(--selected-table-row-color)' }} />

            <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.2, backgroundColor: 'rgba(0,0,0,0.015)' }}>
                <Stack direction="row" justifyContent="flex-end">
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={() => { void loadPlans(); }}
                        disabled={loading || saving}
                        className="button-color"
                        sx={{ minWidth: 120 }}
                    >
                        {t('REFRESH_BUTTON_TEXT')}
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
            {loading ? (
                <Stack spacing={1.8} alignItems="center" sx={{ py: 5 }}>
                    <CircularProgress size={36} sx={{ color: 'var(--button-background-color)' }} />
                    <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 600 }}>
                        {t('PROFILE_LOADING_SUBSCRIPTION_DETAILS')}
                    </Typography>
                </Stack>
            ) : plans.length === 0 ? (
                <Box
                    sx={{
                        py: 6,
                        px: 3,
                        textAlign: 'center',
                        borderRadius: 3,
                        border: '1px dashed var(--selected-table-row-color)',
                        backgroundColor: 'action.hover',
                    }}
                >
                    <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 500, fontSize: '0.95rem' }}>
                        {t('PROFILE_NO_AVAILABLE_PLANS')}
                    </Typography>
                </Box>
            ) : (
                <DataGrid
                    rows={plans}
                    getRowId={(row) => row.stripePriceId}
                    columns={columns}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    componentsProps={{
                        pagination: {
                            labelRowsPerPage: t('ROWS_PER_PAGE_TEXT'),
                        },
                    }}
                    disableRowSelectionOnClick
                    disableColumnSelector
                    disableDensitySelector
                    sx={{
                        borderColor: 'var(--selected-table-row-color)',
                        borderRadius: 2.5,
                        overflow: 'hidden',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'var(--card-header-background-color)',
                            color: 'var(--card-header-text-color)',
                            borderBottom: '2px solid var(--selected-table-row-color)',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 700,
                            fontSize: '0.85rem',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                            fontSize: '0.875rem',
                        },
                        '& .MuiDataGrid-row': {
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'var(--selected-table-row-color)',
                            },
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '2px solid var(--selected-table-row-color)',
                            backgroundColor: 'action.hover',
                            minHeight: 56,
                        },
                        '& .MuiTablePagination-root': {
                            color: 'var(--link-text-color)',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontWeight: 500,
                            fontSize: '0.85rem',
                        },
                    }}
                />
            )}
            </Box>
        </Card>

        <TierPlanEditorDialog
            open={!!editPlan}
            saving={saving}
            stripePriceId={editPlan?.stripePriceId}
            editCurrency={editCurrency}
            currencySymbol={currencySymbol}
            name={name}
            description={description}
            unitAmount={unitAmount}
            modelsAllowedEnabled={modelsAllowedEnabled}
            modelsAllowed={modelsAllowed}
            trialSupported={trialSupported}
            trialDuration={trialDuration}
            onClose={closeDialog}
            onSave={() => { void handleSave(); }}
            onNameChange={(value) => dispatch(setTierName(value))}
            onDescriptionChange={(value) => dispatch(setTierDescription(value))}
            onUnitAmountChange={(value) => dispatch(setTierUnitAmount(value))}
            onModelsAllowedEnabledChange={(value) => dispatch(setTierModelsAllowedEnabled(value))}
            onModelsAllowedChange={(value) => dispatch(setTierModelsAllowed(value))}
            onTrialSupportedChange={(value) => dispatch(setTierTrialSupported(value))}
            onTrialDurationChange={(value) => dispatch(setTierTrialDuration(normalizeTrialDuration(value)))}
        />
        </Box>
    );
}
